import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { BundleProductModel } from "@/api/mappers/productBundles.mapper";
import { ProductBundlePrice } from "@/components/Product/ProductBundles/components/ProductBundlePrice/productBundlePrice.container";
import { useThunkDispatch } from "@/redux/dispatcher";
import { CartModel } from "@/redux/modules/api/data/cart/@types/cart.model";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { BundleProductComponent } from "../BundleProduct/bundleProduct.component";
import { ProductBundleAddToCartButtonComponent } from "../ProductBundleAddToCartButton/productBundleAddToCartButton.component";

type ProductBundleComponentProps = {
    mainProduct: Product<ProductFieldSet.ProductTile>;
    mainProductAmount: number;
    bundleProducts: Array<BundleProductModel>;
    cart?: CartModel;
    onAddToCart: () => void;
    addToCartLoading: boolean;
};

const ProductBundleComponent = ({
    mainProduct,
    mainProductAmount,
    bundleProducts,
    onAddToCart,
    addToCartLoading
}: ProductBundleComponentProps) => {
    const { t } = useGranite();

    const dispatch = useThunkDispatch();

    const cart = useSelector(CartSelectors.getCart);
    const cartLoading = useSelector(CartSelectors.isLoading);

    const products = [
        {
            product: mainProduct,
            quantity: mainProductAmount
        },
        ...bundleProducts
    ];

    useEffect(() => {
        if (!cart && !cartLoading) {
            dispatch(CartModule.load());
        }
    }, [cart, cartLoading, dispatch]);

    return (
        <section className={"productBundle__bundle"}>
            <div className={"productBundle__bundle__products"}>
                {products.map((bundleProduct, index) => (
                    <>
                        {!!index && (
                            <div className={"productBundle__bundle__separator"}>
                                <i className={"las la-lg la-plus"} />
                            </div>
                        )}
                        <BundleProductComponent
                            key={bundleProduct.product.sku}
                            bundleProduct={bundleProduct}
                        />
                    </>
                ))}
            </div>
            <div
                className={
                    "productBundle__buy px-3 py-2 d-flex justify-content-end align-items-center"
                }
            >
                <p className={"mb-0 flex-grow-1 text-left"}>
                    {t("general.productBundles.combinationPrice")}
                </p>
                <ProductBundlePrice
                    className={"mr-3"}
                    bundleProducts={[
                        {
                            product: mainProduct,
                            quantity: mainProductAmount
                        },
                        ...bundleProducts
                    ]}
                />
                <ProductBundleAddToCartButtonComponent
                    bundleSkus={[
                        mainProduct.sku,
                        ...bundleProducts.map(({ product }) => product.sku)
                    ]}
                    cart={cart}
                    onAddToCart={onAddToCart}
                    addToCartLoading={addToCartLoading}
                />
            </div>
        </section>
    );
};

export { ProductBundleComponent };
